import React, { useEffect, useRef } from "react";
import { drawEffectBars, BarEffect } from "../../game-canvas/effect-bars";

// Dev-only gallery to compare active-power-up status-ring designs for your own
// worm's head. Riffs on concentric arcs / segmented ring / fading rings. Each
// tile shows the design at THREE head sizes (tiny -> big-map, typical, small-map)
// so we can judge readability when zoomed out, with sample effects draining on a
// loop. Route: /fx
const VARIANTS: { n: number; label: string }[] = [
  { n: 1, label: "Concentric arcs — thin" },
  { n: 2, label: "Concentric arcs — thick + glow" },
  { n: 3, label: "Concentric arcs — thick, wide spacing" },
  { n: 4, label: "Segmented — equal division (symmetric)" },
  { n: 5, label: "Segmented — fixed slots, drain length" },
  { n: 6, label: "Segmented — fixed slots, drain by fade" },
  { n: 7, label: "Fading rings — opacity" },
  { n: 8, label: "Fading rings — thickness" },
  { n: 9, label: "Segmented — fixed slots, thick + glow" },
  { n: 10, label: "Half circles — green bottom / red top" },
  { n: 11, label: "Half circles — green right / red left" },
  { n: 12, label: "Half circles — despawn left → right" },
  { n: 13, label: "Segmented — fixed 1/3 slots, never reflow" },
  { n: 14, label: "Half circles — green L→R, red R→L" },
];

// Sample active effects (good/bad) each draining on its own loop.
const SAMPLE = [
  { good: true, period: 5.0, offset: 0 },
  { good: false, period: 3.4, offset: 1.3 },
  { good: true, period: 6.6, offset: 3.0 },
];

// Head radii in px: tiny (big map, worm ~2-3px), typical, and small-map.
const SIZES = [3, 6, 12];
const TILE_W = 300;
const TILE_H = 150;
const CENTERS = [58, 150, 245]; // x for the 3 heads

export default function EffectBarsDemo() {
  const canvasRefs = useRef<(HTMLCanvasElement | null)[]>([]);
  const rafRef = useRef<number>(0);
  const startRef = useRef<number>(0);

  useEffect(() => {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    canvasRefs.current.forEach((c) => { if (c) { c.width = TILE_W * dpr; c.height = TILE_H * dpr; } });

    function loop(now: number) {
      if (!startRef.current) startRef.current = now;
      const t = (now - startRef.current) / 1000;
      const effects: BarEffect[] = SAMPLE.map((s) => ({ good: s.good, frac: 1 - (((t + s.offset) % s.period) / s.period) }));
      const cy = TILE_H * 0.5;

      VARIANTS.forEach((v, idx) => {
        const c = canvasRefs.current[idx];
        const ctx = c && c.getContext("2d");
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, TILE_W, TILE_H);
        SIZES.forEach((headR, si) => {
          const cx = CENTERS[si];
          // Worm-head: a short trail stub + the head, like the game.
          ctx.strokeStyle = "#37c8e0";
          ctx.lineWidth = headR * 1.5;
          ctx.lineCap = "round";
          ctx.beginPath();
          ctx.moveTo(cx - headR * 2.6, cy + headR * 0.4);
          ctx.lineTo(cx, cy);
          ctx.stroke();
          ctx.fillStyle = "#eaf6ff";
          ctx.beginPath();
          ctx.arc(cx, cy, headR, 0, Math.PI * 2);
          ctx.fill();
          drawEffectBars(ctx, cx, cy, headR, effects, v.n);
        });
      });
      rafRef.current = window.requestAnimationFrame(loop);
    }
    rafRef.current = window.requestAnimationFrame(loop);
    return () => window.cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div style={{ maxWidth: 1000, margin: "0 auto", padding: "8px 16px 60px", fontFamily: "'Rajdhani', sans-serif", color: "#eaf0ff" }}>
      <h1 style={{ fontFamily: "'Orbitron', sans-serif", letterSpacing: 2, fontSize: 22 }}>Effect ring designs</h1>
      <p style={{ color: "#8995b3", fontSize: 14, marginTop: 4 }}>
        Status rings on your own worm's head. Green = a buff on you, red = a debuff, draining as it runs out.
        Each tile shows the design at three head sizes: <b>tiny (big-map)</b>, typical, and small-map. A
        minimum radius keeps it readable even on the tiny head. Sample: 2 buffs + 1 debuff, looping.
      </p>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))", gap: 14, marginTop: 16 }}>
        {VARIANTS.map((v, idx) => (
          <div key={v.n} style={{ background: "#0b0f17", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 10, overflow: "hidden" }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, padding: "8px 11px" }}>
              <span style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, color: "#37c8e0", fontSize: 15 }}>{v.n}</span>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: "#c3ccdf" }}>{v.label}</span>
            </div>
            <canvas
              ref={(el) => { canvasRefs.current[idx] = el; }}
              style={{ width: TILE_W, height: TILE_H, display: "block", background: "radial-gradient(120% 100% at 50% 40%, #131b2e 0%, #0a0e16 100%)" }}
            />
          </div>
        ))}
      </div>
    </div>
  );
}
